import { useEffect } from 'react';
import { isMarketSettled, isOutcomeSettled, useMarketRealtime } from '@/lib/realtime';
import { isClosed } from '@/lib/snake-market';

/** While stuck on a closed instance, re-poll the series this often (ms). */
const RETRY_INTERVAL_MS = 2500;

/** The current instance the hook advances away from when it closes. */
export interface SnakeAutoAdvanceTarget {
  /** Internal market id (cuid) of the current instance — resolves realtime. */
  marketId: string | null | undefined;
  /** Plaee external market id — resolves the Centrifugo market channel. */
  externalMarketId: string | null | undefined;
  /** ISO close time of the current instance — drives the clock backup. */
  closeTime: string | null | undefined;
}

/**
 * Auto-advance a snake series to the next live instance when the current one
 * closes — driven by realtime, backed by the wall clock.
 *
 * Rotation is always a refetch (the series endpoint returns the current /
 * upcoming instance for an instrument+resolution), but the *trigger* comes from
 * two independent sources so the advance is both instant and guaranteed:
 *
 * 1. **Realtime (primary)** — the market channel's `outcome_updated`
 *    (`Closed` / settled) and `market_updated` (settled) fire at the real
 *    exchange close, which beats a scheduled `closeTime` that can drift. Reacts
 *    within a tick.
 * 2. **Clock (backup)** — once `closeTime` passes we refetch and then keep
 *    polling every {@link RETRY_INTERVAL_MS} until a live instance replaces the
 *    closed one. Guarantees advance even if realtime is disconnected, the close
 *    event is missed, or the next instance opens only after a gap.
 *
 * Both paths call the same `refetch`; React Query coalesces concurrent refetches
 * of the same key, so there's no double-fetch. The realtime subscription is
 * ref-counted by the RealtimeManager, so it shares the underlying Centrifuge
 * subscription the chart already opened for this market — no extra socket, no
 * churn, and the untouchable chart subscriber is left alone.
 *
 * Returns `isAdvancing` — true while past close and still awaiting the live
 * instance — so the UI can show a "next round" transition instead of a dead
 * `00:00` countdown.
 *
 * TODO(snake-next-market): if a dedicated "next instance" contract lands,
 * switch to it for a seamless hand-off (and to advance the chart window).
 */
export function useSnakeAutoAdvance(
  target: SnakeAutoAdvanceTarget,
  nowMs: number,
  refetch: () => void,
): { isAdvancing: boolean } {
  const { marketId, externalMarketId, closeTime } = target;
  const closed = isClosed(closeTime, nowMs);

  // Realtime (primary): advance the instant the exchange reports this market
  // closed or settled. `useMarketRealtime` keeps the handlers in a ref, so the
  // inline closures are fine; it no-ops when the ids are missing.
  useMarketRealtime(marketId, externalMarketId, {
    onOutcomeUpdated: (e) => {
      if (isOutcomeSettled(e)) refetch();
    },
    onMarketUpdated: (e) => {
      if (isMarketSettled(e)) refetch();
    },
  });

  // Clock (backup): refetch on close, then poll until a live instance arrives
  // (which flips `closed` false and clears the interval).
  useEffect(() => {
    if (!closed) return;
    refetch();
    const id = setInterval(refetch, RETRY_INTERVAL_MS);
    return () => clearInterval(id);
  }, [closed, refetch]);

  return { isAdvancing: closed };
}
